import { alpha, darken, lighten } from '@mui/material/styles';
import { isLightTheme, resolveSemanticColor } from '@/theme/semanticTokens';
import { s, typographyTokens } from '@/theme/tokens';
import { getChipPaletteForMode } from '@/ui/primitives/statusChips';
import type { TooltipProps } from '@/ui';

export const RELATIVE_TIME_TEXT_SX = {
  display: 'block',
  width: '100%',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const SUITE_NAME_TO_TAGS_GAP = s('none');

export const LATEST_META_DETAIL_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: s('xs'),
  pl: s('xxs'),
  width: '100%',
  minWidth: 0,
  overflow: 'hidden',
};

export const LATEST_META_DETAIL_VALUE_SX = {
  display: 'block',
  minWidth: 0,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

export const LATEST_META_DETAIL_TEXT_SX = {
  '&&': {
    fontSize: '13px',
    fontWeight: typographyTokens.captionSmall.fontWeight,
    lineHeight: 1.05,
  },
};

export const CLONE_TOOLTIP_SLOT_PROPS: TooltipProps['slotProps'] = {
  tooltip: {
    sx: {
      fontSize: '15px',
      lineHeight: 1.4,
    },
  },
};

export const resolveLatestChipForeground = (
  theme: Parameters<typeof resolveSemanticColor>[0],
  statusTone: string,
): string => {
  const isLightMode = isLightTheme(theme);
  const greyChipPalette = getChipPaletteForMode(isLightMode ? 'light' : 'dark', 'grey');
  const greyLatestForeground: string = darken(
    greyChipPalette.background,
    isLightMode ? 0.88 : 0.92,
  );
  if (isLightMode) {
    if (statusTone === 'blue' || statusTone === 'info') {
      return darken(resolveSemanticColor(theme, 'info'), 0.5);
    }
    if (statusTone === 'green' || statusTone === 'success') {
      return darken(resolveSemanticColor(theme, 'success'), 0.5);
    }
    if (statusTone === 'red' || statusTone === 'error') {
      return darken(resolveSemanticColor(theme, 'error'), 0.5);
    }
    if (statusTone === 'orange' || statusTone === 'warning') {
      return darken(resolveSemanticColor(theme, 'warning'), 0.52);
    }
    if (statusTone === 'brown') {
      return darken(resolveSemanticColor(theme, 'brown'), 0.52);
    }
    if (statusTone === 'grey' || statusTone === 'neutral') {
      return greyLatestForeground;
    }
    return darken(resolveSemanticColor(theme, 'textPrimary'), 0.24);
  }
  if (statusTone === 'blue' || statusTone === 'info') {
    return darken(resolveSemanticColor(theme, 'info'), 0.72);
  }
  if (statusTone === 'green' || statusTone === 'success') {
    return darken(resolveSemanticColor(theme, 'success'), 0.72);
  }
  if (statusTone === 'red' || statusTone === 'error') {
    return darken(resolveSemanticColor(theme, 'error'), 0.72);
  }
  if (statusTone === 'orange' || statusTone === 'warning') {
    return darken(resolveSemanticColor(theme, 'warning'), 0.74);
  }
  if (statusTone === 'brown') {
    return darken(resolveSemanticColor(theme, 'brown'), 0.74);
  }
  if (statusTone === 'grey' || statusTone === 'neutral') {
    return greyLatestForeground;
  }
  return darken(resolveSemanticColor(theme, 'textPrimary'), 0.56);
};

export const resolveLatestChipBackground = (
  theme: Parameters<typeof resolveSemanticColor>[0],
  statusTone: string,
): string => {
  const isLightMode = isLightTheme(theme);
  const greyChipPalette = getChipPaletteForMode(isLightMode ? 'light' : 'dark', 'grey');
  const greyLatestBackground = isLightMode
    ? lighten(greyChipPalette.background, 0.05)
    : lighten(greyChipPalette.background, 0.09);
  if (isLightMode) {
    if (statusTone === 'blue' || statusTone === 'info') {
      return alpha(resolveSemanticColor(theme, 'info'), 0.34);
    }
    if (statusTone === 'green' || statusTone === 'success') {
      return alpha(resolveSemanticColor(theme, 'success'), 0.34);
    }
    if (statusTone === 'red' || statusTone === 'error') {
      return alpha(resolveSemanticColor(theme, 'error'), 0.32);
    }
    if (statusTone === 'orange' || statusTone === 'warning') {
      return alpha(resolveSemanticColor(theme, 'warning'), 0.34);
    }
    if (statusTone === 'brown') {
      return alpha(resolveSemanticColor(theme, 'brown'), 0.34);
    }
    if (statusTone === 'grey' || statusTone === 'neutral') {
      return greyLatestBackground;
    }
    return alpha(resolveSemanticColor(theme, 'surfaceSubtle'), 0.72);
  }
  if (statusTone === 'blue' || statusTone === 'info') {
    return darken(resolveSemanticColor(theme, 'info'), 0.23);
  }
  if (statusTone === 'green' || statusTone === 'success') {
    return darken(resolveSemanticColor(theme, 'success'), 0.23);
  }
  if (statusTone === 'red' || statusTone === 'error') {
    return darken(resolveSemanticColor(theme, 'error'), 0.23);
  }
  if (statusTone === 'orange' || statusTone === 'warning') {
    return darken(resolveSemanticColor(theme, 'warning'), 0.21);
  }
  if (statusTone === 'brown') {
    return darken(resolveSemanticColor(theme, 'brown'), 0.21);
  }
  if (statusTone === 'grey' || statusTone === 'neutral') {
    return greyLatestBackground;
  }
  return darken(resolveSemanticColor(theme, 'surfaceSubtle'), 0.12);
};
